import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useStartSyncMutation, useGetSyncJobQuery } from "../services/alegraSync/alegraSync";
import { itemsApi } from "../services/items/items";
import { RootState } from "../app/store";
import { SYNC_TERMINAL_STATUSES, SyncJobType } from "../types/types";
import { ALEGRA_SYNC_TEXTS, SYNC_POLL_INTERVAL_MS } from "../pages/shared/constants";

// Owns one sync run: starting it, following it, and telling the item cache when it is over.
//
// It lives outside the dialog on purpose. The run continues in a Lambda whether or not anyone is
// looking at it, so closing the dialog must not lose the job — and the toolbar button needs to know
// a job is in flight in order to disable itself.

const HTTP_FORBIDDEN = 403;
const HTTP_CONFLICT = 409;

const isTerminalStatus = (status?: string) =>
  !!status && (SYNC_TERMINAL_STATUSES as readonly string[]).includes(status);

const startErrorMessage = (status: unknown): string => {
  if (status === HTTP_CONFLICT) return ALEGRA_SYNC_TEXTS.errorAlreadyRunning;
  if (status === HTTP_FORBIDDEN) return ALEGRA_SYNC_TEXTS.errorForbidden;
  return ALEGRA_SYNC_TEXTS.errorStartFailed;
};

export type AlegraSyncState = {
  job: SyncJobType | undefined;
  jobId: string | null;
  isStarting: boolean;
  isRunning: boolean;
  isFinished: boolean;
  /** Blocks the trigger until the bearer token has landed — see the skip note below. */
  isAuthReady: boolean;
  error: string | null;
  start: () => Promise<void>;
  reset: () => void;
};

export const useAlegraSync = (): AlegraSyncState => {
  const dispatch = useDispatch();

  // `POST /alegra-sync` and `GET /alegra-sync/{jobId}` both sit behind the custom authorizer, so
  // they need the bearer token that `useUpdateAuthToken` fetches asynchronously. Firing before it
  // lands produces a 401 on the very first request — the gotcha documented in CLAUDE.md and hit by
  // `alegraItemsApi` before it started skipping.
  const { token, isLoading: isAuthLoading } = useSelector((state: RootState) => state.auth);
  const isAuthReady = !isAuthLoading && !!token;

  const [jobId, setJobId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const invalidatedFor = useRef<string | null>(null);

  const [startSync, { isLoading: isStarting }] = useStartSyncMutation();

  // Polling has to stop once the job is terminal, rather than hammering the endpoint forever behind
  // a dialog someone left open. It is held as state instead of derived inline because the query
  // options cannot reference the query's own result.
  const [pollingStopped, setPollingStopped] = useState(false);

  const { data: job, isError: isPollError } = useGetSyncJobQuery(jobId as string, {
    skip: !jobId || !isAuthReady,
    pollingInterval: pollingStopped ? 0 : SYNC_POLL_INTERVAL_MS,
  });

  const isFinished = isTerminalStatus(job?.status);
  const isRunning = !!jobId && !isFinished;

  useEffect(() => {
    // One render behind the terminal status, which costs at most a single extra GET of a row that
    // has stopped changing.
    setPollingStopped(isFinished);
  }, [isFinished]);

  useEffect(() => {
    if (!job || !isTerminalStatus(job.status)) return;
    // Once per job: the query keeps returning the same terminal row, and re-invalidating on every
    // render would refetch the whole catalog in a loop.
    if (invalidatedFor.current === job.jobId) return;
    invalidatedFor.current = job.jobId;

    // A dry run wrote nothing, so the grid has nothing to re-read.
    if (job.status === "completed" && !job.dry_run) {
      dispatch(itemsApi.util.invalidateTags(["Item"]));
    }
  }, [job, dispatch]);

  const start = useCallback(async () => {
    setError(null);
    try {
      const response = await startSync().unwrap();
      setJobId(response.jobId);
    } catch (err) {
      // 409 is the single-flight lock refusing a second run, and it is the one an admin is most
      // likely to trigger — two people clicking sync at once. It deserves its own sentence rather
      // than a generic failure.
      setError(startErrorMessage((err as { status?: unknown })?.status));
    }
  }, [startSync]);

  const reset = useCallback(() => {
    setJobId(null);
    setError(null);
    setPollingStopped(false);
  }, []);

  const combinedError = useMemo(() => {
    if (error) return error;
    if (isPollError) return ALEGRA_SYNC_TEXTS.errorPollFailed;
    // A rejected job means the worker itself found the lock taken — the same situation as a 409,
    // reported from the other side.
    if (job?.status === "rejected") return ALEGRA_SYNC_TEXTS.errorAlreadyRunning;
    if (job?.status === "failed") return ALEGRA_SYNC_TEXTS.errorJobFailed(job.error);
    return null;
  }, [error, isPollError, job]);

  return {
    job,
    jobId,
    isStarting,
    isRunning,
    isFinished,
    isAuthReady,
    error: combinedError,
    start,
    reset,
  };
};

export default useAlegraSync;
